import config from 'constants/config'
import type {
  IComponentContentFields,
  IComponentTopBannerFields,
  ILegalPageFaqFields,
} from '@luxuryescapes/contract-svc-content'
import { nonNullable } from 'lib/array/arrayUtils'
import { isRecord } from 'lib/object/objectUtils'
import { isString } from 'lib/string/stringUtils'
import qs from 'qs'
import {
  TYPE_OF_PAGE_DESTINATION,
  TYPE_OF_PAGE_HOLIDAY,
  TYPE_OF_PAGE_BNBL,
  TYPE_OF_PAGE_CONTENT_HUB,
} from 'constants/landingPage'
import { SITEWIDE_NOTIFICATION_SLUG } from 'constants/content'
import request from 'api/requestUtils'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { LUXURY_ESCAPES } from 'constants/brands'
import { parseMarkdownSections } from 'lib/string/markdownSections'

type ContentResponseItem<TFields> = {
  fields?: TFields | null
}

type DestinationProductPageSourceItem = Partial<
  Pick<ILegalPageFaqFields, 'title' | 'answer' | 'slug' | 'tags'>
  & Pick<IComponentContentFields, 'description'>
> & {
  contentTypeId?: string
}

type DestinationProductPageSourceFields = {
  pageContent?: Array<DestinationProductPageSourceItem | null> | null
}

type DestinationProductPageItem =
  App.DestinationProductFaqContent['pageContent'][number]
  & Partial<Pick<IComponentContentFields, 'description'>>

type TopBannerResponseFields = Partial<
  Pick<IComponentTopBannerFields, 'slug' | 'show' | 'openInNewTab' | 'openInPopup' | 'bannerText' | 'linkText' | 'linkTo'>
> & Partial<Pick<App.NotificationBanner, 'image'>>

function getFirstFields<TFields extends object>(
  data: App.ApiResponse<Array<ContentResponseItem<TFields>>>,
): TFields | null {
  const fields = data.result?.[0]?.fields
  return isRecord(fields) ? fields as TFields : null
}

function mapDestinationProductPageItem(item: unknown): DestinationProductPageItem | null {
  if (!isRecord(item)) {
    return null
  }

  const title = isString(item.title) ? item.title.trim() : undefined
  const answer = isString(item.answer) ? item.answer : undefined
  const description = isString(item.description) ? item.description : undefined

  if (!title || !(answer?.trim() || description?.trim())) {
    return null
  }

  return {
    title,
    answer: answer ?? '',
    slug: isString(item.slug) ? item.slug : '',
    tags: Array.isArray(item.tags) ? item.tags.filter(isString) : [],
    contentTypeId: isString(item.contentTypeId) ? item.contentTypeId : '',
    ...(description ? { description } : {}),
  }
}

function mapNotificationBanner(fields: TopBannerResponseFields | null, fallbackSlug: string): App.NotificationBanner | null {
  if (!fields) {
    return null
  }

  const bannerText = isString(fields.bannerText) ? fields.bannerText : undefined
  if (!bannerText?.trim()) {
    return null
  }

  const slug = isString(fields.slug) ? fields.slug : fallbackSlug

  return {
    id: slug,
    slug,
    bannerText,
    linkText: isString(fields.linkText) ? fields.linkText : undefined,
    linkTo: isString(fields.linkTo) ? fields.linkTo : undefined,
    openInNewTab: typeof fields.openInNewTab === 'boolean' ? fields.openInNewTab : false,
    openInPopup: typeof fields.openInPopup === 'boolean' ? fields.openInPopup || undefined : undefined,
    show: typeof fields.show === 'boolean' ? fields.show : false,
    image: fields.image,
  }
}

function destinationPageMap(page: any) {
  const intl = getCurrentIntl()
  const { fields } = page
  const search = fields.locations
  return {
    title: fields.title,
    titleSuffix: 'Escapes',
    subTitle: fields.destinationSubtitle,
    metaTitle: fields.metaTitle || '',
    metaDescription: fields.metaDescription || '',
    slug: fields.slug,
    image: fields.headerImage,
    search: config.hasMagazine && search,
    filters: {
      placeIds: fields.placeIds ?? [ANYWHERE_PLACE_ID],
      useUnifiedSearchEndpoint: true,
    },
    content: {
      title: fields.content ? fields.content.title : '',
      description: fields.content ? fields.content.description : '',
      video: fields.content ? fields.content.videoUrl : '',
      subscriptionTitle: intl.formatMessage(
        {
          id: 'VnikLUDbDrBLCLue8k9q',
          defaultMessage: 'Subscribe to receive exclusive hotel offers to {destination} and other amazing destinations',
          description: 'Email signup overlay title on destination landing pages with the destination name inserted',
        },
        { destination: page.fields.title },
      ),
      subscriptionButton: intl.formatMessage({
        id: 'vjXbr7DuJbRr6fS9MAdR',
        defaultMessage: 'Sign me up',
        description: 'CTA button text for destination page email subscription forms',
      }),
    },
    backgroundColour: fields.backGroundColour,
  }
}

function holidayPageMap(page: any): App.ContentHolidayPage {
  const intl = getCurrentIntl()
  const { fields } = page
  const search = fields.types
  return {
    title: fields.title,
    titleSuffix: '',
    subTitle: fields.subTitle,
    metaTitle: fields.metaTitle || '',
    metaDescription: fields.metaDescription || '',
    slug: fields.slug,
    image: fields.headerImage,
    types: fields.types,
    search: config.hasMagazine && search,
    hideSignUpOverlay: fields.hideSignUpOverlay || false,
    filters: {
      holidayTypes: search,
    },
    content: {
      title: fields.content ? fields.content.title : '',
      description: fields.content ? fields.content.description : '',
      video: fields.content ? fields.content.videoUrl : '',
      subscriptionTitle: intl.formatMessage({
        id: 'KdDZkPVkOuYIHVSEakN_',
        defaultMessage: 'Subscribe to receive exclusive hotel offers to amazing destinations',
        description: 'Email signup overlay title on holiday landing pages without a specific destination',
      }),
      subscriptionButton: intl.formatMessage({
        id: 'vjXbr7DuJbRr6fS9MAdR',
        defaultMessage: 'Sign me up',
        description: 'CTA button text for destination page email subscription forms',
      }),
    },
  }
}

function bnblTypeLandingPageMap(page: any): App.ContentBNBLPage {
  const { fields } = page
  return {
    header: {
      title: fields.title,
      subTitle: fields.subTitle,
      metaTitle: fields.metaTitle,
      metaDescription: fields.metaDescription,
      image: fields.headerImage,
    },
    empowerment: {
      title: fields.empowermentTitle,
      description: fields.empowermentDescription,
    },
    howItWorks: {
      title: fields.howItWorksTitle,
      description: fields.howItWorksTiles.map((item: any) => ({
        icon: {
          title: item.icon.fields.title,
          url: 'https:' + item.icon.fields.file.url,
        },
        title: item.title,
        text: item.text,
      })),
    },
    plus: {
      title: fields.plusTitle,
    },
    faq: fields.faq ? {
      title: fields.faq.title,
      slug: fields.faq.slug,
    } : undefined,
    offerList: {
      title: fields.offerListTitle,
      btnText: fields.offerListButtonText,
      numberOfOffers: fields.numberOfOffers,
    },
    backgroundColour: fields.backGroundColour,
  }
}

function aboutUsPageMap(page: any) {
  const { fields } = page
  return {
    page_title: fields.pageTitle,
    sections: fields.sections.map(
      (item: any) => ({
        link_label: item.linkLabel,
        hash: item.hash,
      }),
    ),
    vision: {
      title: fields.visionTitle,
      section_intro: fields.visionSectionIntro,
      content: {
        title: fields.visionContentTitle,
        steps: fields.visionContentSteps.map((item: any) => ({ step_text: item }),
        ),
      },
    },
    company: {
      title: fields.companyTitle,
      section_intro: fields.companySectionIntro,
      content: fields.companyContent,
    },
    leadership: {
      title: fields.leadershipTitle,
      section_intro: fields.leadershipSectionIntro,
      leaders: fields.leadershipLeaders,
    },
    careers: {
      title: fields.careersTitle,
      section_intro: fields.careersSectionIntro,
      content: fields.careersContent,
      mission: fields.careersMission,
      values: fields.careersValues,
    },
    media: {
      title: fields.mediaTitle,
      content: fields.mediaContent,
      articles: fields.mediaArticles,
    },
  }
}

export function bannerGroupMapping(data: any): App.ComponentBannerGroup {
  const { fields } = data
  return {
    contentTypeId: 'componentBannerGroup',
    title: fields.title,
    subtitle: fields.subtitle,
    slug: fields.slug,
    banners: fields.banners.map((banner: any) => ({
      title: banner.title,
      image: { id: banner.imageId },
      linkTo: banner.linkTo,
      footerText: banner.footerText,
      footerSubText: banner.footerSubText,
      backgroundColor: banner.backgroundColor,
      labels: banner.labels ? banner.labels.map((label: string) => label) : undefined,
    })),
  }
}

function getDestinationPage(slug: string) {
  return request.get<App.ApiResponse<any>>(`/api/content/destinationPage/${slug}`)
    .then(data => {
      if (data.result?.length > 0) {
        return destinationPageMap(data.result[0])
      }
      return null
    })
}

function getHolidayPage(slug: string) {
  return request.get<App.ApiResponse<any>>(`/api/content/holidayPage/${slug}`)
    .then(data => {
      if (data.result?.length > 0) {
        return holidayPageMap(data.result[0])
      }
      return null
    })
}

export function getBannerGroup(slug: string, regionCode: string) {
  const query = qs.stringify({
    region: regionCode,
  })
  return request.get<App.ApiResponse<any>>(`/api/content/bannerGroup/${slug}?${query}`)
    .then(data => {
      if (data.result?.length > 0) {
        return bannerGroupMapping(data.result[0])
      }
      return null
    })
}

export async function getMerchandiseHeroCarousel(slug: string): Promise<Array<App.MerchandiseHeroPromo>> {
  const { result } = await request.get<App.ApiResponse<any>>(`/api/content/merchandiseHeroCarousel/${slug}`)
  if (result?.length > 0) {
    const { fields } = result[0]
    if (fields?.items) {
      return fields.items.map((item: any) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        image: { id: item.imageId, title: item.imageTitle },
        mobileImage: item.mobileImageId ? { id: item.mobileImageId, title: item.imageTitle } : undefined,
        brandImage: item.brandImageId ? { id: item.brandImageId, title: item.brandImageTitle } : undefined,
        buttonText: item.buttonText,
        to: item.buttonUrl,
      }))
    }
  }

  return []
}

export function getLandingPage(slug: string, type: string, regionCode: string): Promise<any> {
  switch (type) {
    case TYPE_OF_PAGE_DESTINATION:
      return getDestinationPage(slug)
    case TYPE_OF_PAGE_HOLIDAY:
      return getHolidayPage(slug)
    case TYPE_OF_PAGE_BNBL:
      return getBNBLTypeLandingPage(slug)
    case TYPE_OF_PAGE_CONTENT_HUB:
      return getContentHubPage(slug, regionCode)
    default:
      return Promise.resolve()
  }
}

export function getAboutUsPage() {
  return request.get<App.ApiResponse<any>>('/api/content/aboutUs')
    .then(data => {
      if (data.result?.length > 0) {
        return aboutUsPageMap(data.result[0])
      }
      return null
    })
}

export function getLegalPage(slug: string): Promise<App.LegalFaqContent | null> {
  return request.get<App.ApiResponse<any>>(`/api/content/legalPages/${slug}`)
    .then((data): App.LegalFaqContent | null => {
      if (data.result?.length > 0) {
        return {
          title: data.result[0].fields.title,
          pageContent: data.result[0].fields.pageContent.map((page: any) => ({

            title: page.title,
            slug: page.slug,
            tags: page.tags,
            contentTypeId: page.contentTypeId,
            description: page.description,
            answer: page.answer,
          })),
          backgroundColour: data.result[0].fields.backGroundColour,
        }
      } else {
        return null
      }
    })
}

// The Contentful content type the migrated legal page sections came from.
const LEGAL_SECTION_CONTENT_TYPE_ID = 'legalTermAndCondition'

interface TermsAndConditionsResult {
  brand: string
  slug: string
  locale: string
  title: string
  body: string
  updatedAt: string
}

/**
 * Luxury Escapes legal pages come from the svc-content `terms_and_conditions`
 * table rather than Contentful. svc-content negotiates the translation from
 * `Accept-Language`, which `localeLoader` already sets, falling back to en-AU.
 *
 * The table holds one markdown document per page, so it is split back into the
 * sections the page was assembled from.
 */
export function getTermsAndConditionsPage(slug: string): Promise<App.LegalFaqContent> {
  return request.get<App.ApiResponse<TermsAndConditionsResult>>(`/api/content/terms-and-conditions/${LUXURY_ESCAPES}/${slug}`)
    .then(data => ({
      title: data.result.title,
      pageContent: parseMarkdownSections(data.result.body).map(section => ({
        title: section.title,
        slug: section.slug,
        tags: [],
        contentTypeId: LEGAL_SECTION_CONTENT_TYPE_ID,
        description: section.description,
      })),
    }))
}

/**
 * Legal page content for the current brand. Slugs not yet migrated into
 * svc-content, and every brand other than Luxury Escapes, come from Contentful.
 */
export function getLegalPageContent(slug: string): Promise<App.LegalFaqContent | null> {
  // Luxury Escapes is unprefixed in Contentful; every other brand is prefixed.
  if (config.BRAND !== LUXURY_ESCAPES) {
    return getLegalPage(`${config.BRAND}-${slug}`)
  }

  return getTermsAndConditionsPage(slug).catch(error => {
    if (error?.status === 404) {
      return getLegalPage(slug)
    }
    throw error
  })
}

export function getDestinationProductPage(slug: string) {
  return request.get<App.ApiResponse<Array<ContentResponseItem<DestinationProductPageSourceFields>>>>(`/api/content/pageDestinationProduct/${slug}`)
    .then(data => {
      const fields = getFirstFields(data)
      if (!fields) {
        return null
      }

      return {
        pageContent: nonNullable((fields.pageContent ?? []).map(mapDestinationProductPageItem)),
      }
    })
    .catch(() => null)
}

export function getNotificationBanner(slug: string, regionCode: string): Promise<App.NotificationBanner | null | undefined> {
  if (slug === SITEWIDE_NOTIFICATION_SLUG) {
    return request.get<App.ApiResponse<Array<ContentResponseItem<TopBannerResponseFields>>>>(`/api/content/componentTopBanner?region=${regionCode}&tags=${slug}`)
      .then(data => {
        return mapNotificationBanner(getFirstFields(data), slug)
      })
      .catch(e => {
        if (e.status === 404) {
          return null
        }
        throw new Error(e)
      })
  }

  return request.get<App.ApiResponse<Array<ContentResponseItem<TopBannerResponseFields>>>>(`/api/content/componentTopBanner/${slug}?region=${regionCode}`)
    .then(data => {
      return mapNotificationBanner(getFirstFields(data), slug)
    })
    .catch(e => {
      if (e.status === 404) {
        return null
      }
      throw new Error(e)
    })
}

export function getContentHubPage(slug: string, regionCode: string): Promise<App.ContentHubPage> {
  return request.get<App.ApiResponse<Array<any>>>(`/api/content/${TYPE_OF_PAGE_CONTENT_HUB}/${slug}?region=${regionCode}`)
    .then(data => {
      if (data.result?.length > 0) {
        const article = data.result[0]

        return {
          ...article,
          backgroundColour: article.backGroundColour,
          sections: article.sections.map((section: any) => {
            if (section.contentTypeId === 'legalPages') {
              return {
                ...section,
                pageContent: section.pageContent.map((item: any) => ({
                  type: item.sys.contentType.sys.id,
                  fields: item.fields,
                })),
              }
            }
            return section
          }),
        }
      } else {
        return null
      }
    })
}

export function getBNBLTypeLandingPage(slug: string) {
  return request.get<App.ApiResponse<any>>(`/api/content/bnbl/${slug}`)
    .then(data => {
      if (data.result?.length > 0) {
        return bnblTypeLandingPageMap(data.result[0])
      }
      return null
    })
}

export function getContentTag(slug: string): Promise<App.ContentTag | null> {
  return request.get<App.ApiResponse<Array<App.ContentTag>>>(`/api/content/componentTag/${slug}`, { excludeBrand: true })
    .then(data => {
      if (data.result?.length > 0) {
        return data.result[0]
      }
      return null
    })
    .catch(e => {
      if (e.status === 404) {
        return null
      }
      throw new Error(e)
    })
}
